import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Archive,
  CircleAlert,
  Boxes,
  BriefcaseBusiness,
  ChevronLeft,
  Cloud,
  CloudOff,
  Euro,
  FileClock,
  Images,
  LayoutGrid,
  LogOut,
  Menu,
  PackagePlus,
  RefreshCw,
  Settings,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { ApiError, api, getApiError } from "./api";
import { Button, IconButton } from "./components/ui";
import { useAsync } from "./hooks";
import { FinancePage } from "./pages/FinancePage";
import { HomePage } from "./pages/HomePage";
import { JobsAuditPage } from "./pages/JobsAuditPage";
import { ListingsPage } from "./pages/ListingsPage";
import { MediaStudioPage } from "./pages/MediaStudioPage";
import { ProductReviewProblemsPage } from "./pages/ProductReviewProblemsPage";
import { ProductReviewsPage } from "./pages/ProductReviewsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { UploaderPage } from "./pages/UploaderPage";
import type { User } from "./types";

type Route =
  | "home"
  | "uploader"
  | "listings"
  | "image-reviews"
  | "safety-reviews"
  | "review-problems"
  | "media"
  | "finance"
  | "templates"
  | "jobs"
  | "settings"
  | "brand-lab";

const pathToRoute: Record<string, Route> = {
  "/": "home",
  "/uploader": "uploader",
  "/aktualisierer": "listings",
  "/bilderpruefung": "image-reviews",
  "/produktsicherheit": "safety-reviews",
  "/probleme-klaeren": "review-problems",
  "/media": "media",
  "/finanzen": "finance",
  "/vorlagen": "templates",
  "/jobs": "jobs",
  "/einstellungen": "settings",
  "/brand-lab": "brand-lab",
};

const routeToPath = Object.fromEntries(
  Object.entries(pathToRoute).map(([path, route]) => [route, path]),
) as Record<Route, string>;

const routeTitles: Record<Route, string> = {
  home: "Aufgaben",
  uploader: "Neuen Artikel anlegen",
  listings: "Artikel & Angebote",
  "image-reviews": "Bilderprüfung",
  "safety-reviews": "Produktsicherheit",
  "review-problems": "Probleme klären",
  media: "Probleme & Bilder",
  finance: "Finanzen & Profit",
  templates: "Vorlagen",
  jobs: "Verlauf & Prüfungen",
  settings: "Einstellungen",
  "brand-lab": "Playzeug Brand Lab",
};

interface ToastMessage {
  id: number;
  tone: "success" | "error" | "info";
  message: string;
}

export interface PageContext {
  navigate: (route: Route, query?: Record<string, string>, options?: { replace?: boolean }) => void;
  notify: (message: string, tone?: ToastMessage["tone"]) => void;
}

export function App() {
  const [authState, setAuthState] = useState<"loading" | "authenticated" | "anonymous">("loading");
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    let active = true;
    api
      .me()
      .then((currentUser) => {
        if (!active) return;
        setUser(currentUser);
        setAuthState("authenticated");
      })
      .catch((error) => {
        if (!active) return;
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          setAuthState("anonymous");
        } else {
          setAuthState("anonymous");
        }
      });
    return () => {
      active = false;
    };
  }, []);

  if (authState === "loading") return <AppBootScreen />;
  if (authState === "anonymous") {
    return (
      <LoginPage
        onAuthenticated={(nextUser) => {
          setUser(nextUser);
          setAuthState("authenticated");
        }}
      />
    );
  }

  return (
    <AuthenticatedApp
      user={user}
      onLogout={() => {
        setUser(null);
        setAuthState("anonymous");
      }}
    />
  );
}

function AppBootScreen() {
  return (
    <main className="boot-screen">
      <div className="brand-mark brand-mark--large" aria-hidden>
        <span>P</span>
      </div>
      <div className="boot-screen__copy">
        <strong>Playzeug Amazon Manager</strong>
        <span>Arbeitsbereich wird vorbereitet …</span>
      </div>
      <span className="boot-loader" />
    </main>
  );
}

function LoginPage({ onAuthenticated }: { onAuthenticated: (user: User) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      onAuthenticated(await api.login(email.trim(), password));
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-screen">
      <div className="login-glow login-glow--one" />
      <div className="login-glow login-glow--two" />
      <section className="login-intro">
        <div className="brand-lockup">
          <div className="brand-mark" aria-hidden>
            <span>P</span>
          </div>
          <div>
            <strong>Playzeug</strong>
            <span>Worker Hub</span>
          </div>
        </div>
        <div className="login-intro__content">
          <p className="eyebrow">Amazon Operations</p>
          <h1>Interner Arbeitsbereich für den Amazon-Betrieb</h1>
          <p>
            Artikel hochladen, Länder-Lücken erkennen und Änderungen kontrolliert mit
            Vorschau, Bestätigung und Live-Prüfung übermitteln.
          </p>
          <div className="login-features">
            <span><ShieldCheck size={17} /> Admin-only</span>
            <span><Cloud size={17} /> Vault-gesichert</span>
            <span><FileClock size={17} /> Vollständig protokolliert</span>
          </div>
        </div>
        <p className="login-intro__legal">Interner Arbeitsbereich · Playzeug</p>
      </section>

      <section className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <div className="login-card__header">
            <div className="brand-mark brand-mark--mobile" aria-hidden>
              <span>P</span>
            </div>
            <h2>Als Administrator anmelden</h2>
            <p>Verwende deinen internen Admin-Zugang.</p>
          </div>

          {error && (
            <div className="alert alert--error" role="alert">
              <CloudOff size={18} />
              <span>{error}</span>
            </div>
          )}

          <label className="field">
            <span className="field__label">E-Mail-Adresse</span>
            <input
              className="input input--large"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="admin@playzeug.de"
              required
              autoFocus
            />
          </label>
          <label className="field">
            <span className="field__label">Passwort</span>
            <input
              className="input input--large"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••••••"
              required
            />
          </label>

          <Button className="button--full button--large" type="submit" loading={loading}>
            Sicher anmelden
          </Button>
          <p className="login-card__security">
            Zugangsdaten werden ausschließlich verschlüsselt an den API Server übertragen.
            Amazon-Secrets bleiben im Vault.
          </p>
        </form>
      </section>
    </main>
  );
}

function AuthenticatedApp({ user, onLogout }: { user: User | null; onLogout: () => void }) {
  const [location, setLocation] = useState(() => window.location.pathname + window.location.search);
  const route = pathToRoute[location.split("?")[0]] ?? "home";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const connection = useAsync(api.connection, []);

  const navigate = useCallback((nextRoute: Route, query?: Record<string, string>, options?: { replace?: boolean }) => {
    const search = query ? new URLSearchParams(query).toString() : "";
    const path = `${routeToPath[nextRoute]}${search ? `?${search}` : ""}`;
    if (options?.replace) window.history.replaceState({}, "", path);
    else window.history.pushState({}, "", path);
    setLocation(path);
    setMobileNavOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const pop = () => setLocation(window.location.pathname + window.location.search);
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, []);

  const notify = useCallback((message: string, tone: ToastMessage["tone"] = "success") => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 4500);
  }, []);

  const context = useMemo<PageContext>(() => ({ navigate, notify }), [navigate, notify]);

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      onLogout();
    }
  };

  const connectionState = connection.error
    ? "disconnected"
    : connection.data?.status ?? "unknown";
  const connectionLabel = connection.loading
    ? "Amazon wird geprüft"
    : connection.error
      ? "Amazon nicht verbunden"
      : {
          connected: "Amazon verbunden",
          degraded: "Amazon eingeschränkt",
          disconnected: "Amazon getrennt",
          unknown: "Amazon nicht eingerichtet",
        }[connectionState];
  const connectionShortLabel = connection.loading
    ? "Prüfen"
    : connection.error
      ? "Offline"
      : {
          connected: "Online",
          degraded: "Eingeschränkt",
          disconnected: "Offline",
          unknown: "Ohne Setup",
        }[connectionState];

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Zum Inhalt springen
      </a>
      <aside className={`sidebar ${mobileNavOpen ? "is-open" : ""}`}>
        <header className="sidebar__header">
          <button className="brand-lockup brand-lockup--button" onClick={() => navigate("home")}>
            <div className="brand-mark" aria-hidden>
              <span>P</span>
            </div>
            <div>
              <strong>Playzeug</strong>
              <span>Amazon Manager</span>
            </div>
          </button>
          <IconButton
            className="sidebar__close"
            label="Navigation schließen"
            onClick={() => setMobileNavOpen(false)}
          >
            <X size={20} />
          </IconButton>
        </header>

        <nav className="sidebar__nav" aria-label="Hauptnavigation">
          <p className="sidebar__section-label">Arbeitsbereich</p>
          <NavButton
            active={route === "home"}
            icon={<LayoutGrid />}
            label="Meine Aufgaben"
            onClick={() => navigate("home")}
          />
          <NavButton
            active={route === "listings"}
            icon={<Boxes />}
            label="Artikel & Angebote"
            badge={undefined}
            onClick={() => navigate("listings")}
          />
          <NavButton
            active={route === "uploader"}
            icon={<PackagePlus />}
            label="Neuen Artikel anlegen"
            onClick={() => navigate("uploader")}
          />
          <NavButton active={route === "image-reviews"} icon={<Images />} label="Bilderprüfung" onClick={() => navigate("image-reviews")} />
          <NavButton active={route === "review-problems"} icon={<CircleAlert />} label="Probleme klären" onClick={() => navigate("review-problems")} />
          <NavButton active={route === "safety-reviews"} icon={<ShieldCheck />} label="Produktsicherheit" onClick={() => navigate("safety-reviews")} />
          <NavButton
            active={route === "finance"}
            icon={<Euro />}
            label="Finanzen & Profit"
            onClick={() => navigate("finance")}
          />
          <details className="nav-tools" open={["media", "templates", "jobs", "settings", "brand-lab"].includes(route) || undefined}>
          <summary>Werkzeuge & Einstellungen</summary>
          <NavButton active={route === "media"} icon={<Images />} label="Probleme & Bilder" onClick={() => navigate("media")} />
          <NavButton
            active={route === "templates"}
            icon={<Archive />}
            label="Vorlagen"
            onClick={() => navigate("templates")}
          />
          <NavButton
            active={route === "jobs"}
            icon={<FileClock />}
            label="Verlauf & Prüfungen"
            onClick={() => navigate("jobs")}
          />
          <NavButton
            active={route === "settings"}
            icon={<Settings />}
            label="Einstellungen"
            onClick={() => navigate("settings")}
          />
          <NavButton
            active={route === "brand-lab"}
            icon={<Sparkles />}
            label="Brand Lab"
            disabled
            badge="Bald"
            onClick={() => navigate("brand-lab")}
          />
          </details>
        </nav>

        <div className="sidebar__footer">
          <div className="sidebar-user">
            <span className="sidebar-user__avatar">
              {(user?.name ?? user?.email ?? "A").slice(0, 1).toUpperCase()}
            </span>
            <span>
              <strong>{user?.name || "Administrator"}</strong>
              <small>{user?.email ?? "Admin-Zugang"}</small>
            </span>
          </div>
          <IconButton label="Abmelden" onClick={logout}>
            <LogOut size={18} />
          </IconButton>
        </div>
      </aside>
      {mobileNavOpen && (
        <button
          className="sidebar-scrim"
          aria-label="Navigation schließen"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <div className="app-main">
        <header className="topbar">
          <div className="topbar__left">
            <IconButton
              className="mobile-menu"
              label="Navigation öffnen"
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu size={21} />
            </IconButton>
            {route !== "home" && (
              <button className="topbar__back" onClick={() => navigate("home")}>
                <ChevronLeft size={17} />
                Aufgaben
              </button>
            )}
            <div className="topbar__heading">
              <span className="topbar__eyebrow">Amazon Manager</span>
              <span className="topbar__title">{routeTitles[route]}</span>
            </div>
          </div>
          <div className="topbar__right">
            <div
              className="connection-status"
              aria-live="polite"
              aria-busy={connection.loading}
              title={connection.error ?? connection.data?.message ?? "Amazon-Verbindungsstatus"}
            >
              <span
                className={`connection-status__dot connection-status__dot--${connectionState}`}
                aria-hidden
              />
              <span className="connection-status__label">{connectionLabel}</span>
              <span className="connection-status__short-label">{connectionShortLabel}</span>
            </div>
            <IconButton
              className="connection-refresh"
              label="Amazon-Verbindung erneut prüfen"
              onClick={() => void connection.reload()}
              disabled={connection.loading}
            >
              <RefreshCw size={13} className={connection.loading ? "spin" : ""} />
            </IconButton>
          </div>
        </header>

        <main id="main-content" className="page-container" key={location}>
          {route === "home" && (
            <HomePage
              {...context}
              connection={connection.data}
              connectionError={connection.error}
              onConnectionReload={connection.reload}
            />
          )}
          {route === "uploader" && <UploaderPage {...context} />}
          {route === "listings" && <ListingsPage {...context} />}
          {route === "image-reviews" && <ProductReviewsPage {...context} kind="images" />}
          {route === "review-problems" && <ProductReviewProblemsPage {...context} />}
          {route === "safety-reviews" && <ProductReviewsPage {...context} kind="safety" />}
          {route === "media" && <MediaStudioPage {...context} />}
          {route === "finance" && <FinancePage {...context} />}
          {route === "templates" && <TemplatesPage {...context} />}
          {route === "jobs" && <JobsAuditPage {...context} />}
          {route === "settings" && <SettingsPage {...context} />}
          {route === "brand-lab" && <BrandLabPage navigate={navigate} />}
        </main>
      </div>

      <div className="toast-stack" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`}>
            <span className="toast__icon">
              {toast.tone === "success" ? "✓" : toast.tone === "error" ? "!" : "i"}
            </span>
            <span>{toast.message}</span>
            <button
              onClick={() =>
                setToasts((current) => current.filter((entry) => entry.id !== toast.id))
              }
              aria-label="Meldung schließen"
            >
              <X size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function NavButton({
  active,
  icon,
  label,
  badge,
  disabled = false,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  badge?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`nav-button ${active ? "is-active" : ""} ${disabled ? "is-disabled" : ""}`}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      aria-disabled={disabled}
    >
      <span className="nav-button__icon">{icon}</span>
      <span className="nav-button__label">{label}</span>
      {badge && <small>{badge}</small>}
    </button>
  );
}

function BrandLabPage({ navigate }: { navigate: (route: Route) => void }) {
  return (
    <div className="brand-lab-page">
      <div className="brand-lab-watermark">
        <Sparkles size={54} />
      </div>
      <div className="brand-lab-status">
        <span className="status-pill status-pill--neutral">Deaktiviert</span>
        <span>Markenfreigabe ausstehend</span>
      </div>
      <div className="brand-lab-content">
        <p className="eyebrow">Playzeug Brand Lab</p>
        <h1>Bundles, Multipacks & eigene Katalogwelten</h1>
        <p>
          Hier entstehen später Playzeug-Bundles aus Sets, Polybags und exklusiven
          Zusammenstellungen. Der gesamte Bereich bleibt bis zur bestätigten
          Amazon-Markenfreigabe bewusst gesperrt.
        </p>
        <div className="brand-lab-preview-grid" aria-hidden>
          <div><BriefcaseBusiness size={25} /><strong>Bundle Builder</strong><span>Sets kombinieren</span></div>
          <div><Sparkles size={25} /><strong>Marken-Content</strong><span>Playzeug Katalog</span></div>
          <div><Archive size={25} /><strong>A+ Vorlagen</strong><span>Brand Story</span></div>
        </div>
        <Button variant="secondary" disabled>
          Markenfreigabe ausstehend
        </Button>
        <button className="text-button" onClick={() => navigate("home")}>
          Zurück zur Modulübersicht
        </button>
      </div>
    </div>
  );
}
